import { JSX } from 'react';
import { Controller, useFormContext } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import TextField from '@mui/material/TextField';
import Typography from '@mui/material/Typography';
import NumericField from 'shared/components/formFields/numeric-field/numeric-field';

import { useConnectorsContextProvider } from 'features/connectors/context/connectors-provider';
import { CLIENT_SECRET_PLACEHOLDER } from 'features/connectors/domain/client-secret-placeholder';
import styles from './connector-settings-data.module.scss';

const INT32_MAX = 2_147_483_647;

const ConnectorSettingsData = () => {
  const { t } = useTranslation('connectors');
  const { connectorDetailsHook } = useConnectorsContextProvider();
  const { control } = useFormContext();
  const isViewing = connectorDetailsHook.viewingMode === 'viewing';
  const clientSecretLastFourCharacters =
    connectorDetailsHook.currentSelectedConnector?.vcareConnectorData?.clientSecretLastFourCharacters ?? '';

  const connectorOptions = {
    organisationId: {
      required: t('details.settings.data.organisationIdRequired'),
      min: { value: 1, message: t('details.settings.data.organisationIdMin') },
      max: { value: INT32_MAX, message: t('details.settings.data.organisationIdMax') },
    },
    clientId: {
      required: t('details.settings.data.clientIdRequired'),
    },
    clientSecret: {
      required: t('details.settings.data.clientSecretRequired'),
    },
  };

  const organisationIdField = (): JSX.Element => (
    <Controller
      name={'vcareConnectorData.organisationId'}
      control={control}
      rules={connectorOptions.organisationId}
      render={({ field, fieldState: { error } }) => (
        <NumericField
          field={field}
          fieldName={'vcareConnectorData.organisationId'}
          label={t('details.settings.data.organisationId')}
          min={1}
          max={INT32_MAX}
          showSpinners={false}
          error={!!error}
          helperText={error?.message ?? ''}
        />
      )}
    />
  );

  const clientIdField = (): JSX.Element => (
    <Controller
      name={'vcareConnectorData.clientId'}
      control={control}
      rules={connectorOptions.clientId}
      render={({ field, fieldState: { error } }) => (
        <TextField
          {...field}
          id={`vcareConnectorData.clientIdInput`}
          label={t('details.settings.data.clientId')}
          variant="outlined"
          error={!!error}
          helperText={error?.message ?? ''}
          fullWidth
        />
      )}
    />
  );

  const clientSecretField = (): JSX.Element => (
    <Controller
      name={'vcareConnectorData.clientSecret'}
      control={control}
      rules={connectorOptions.clientSecret}
      render={({ field, fieldState: { error } }) => (
        <TextField
          {...field}
          onChange={(e) => {
            let incoming = e.target.value;
            if (field.value === CLIENT_SECRET_PLACEHOLDER) {
              incoming = incoming.replace(CLIENT_SECRET_PLACEHOLDER, '');
            }

            field.onChange(incoming);
          }}
          onFocus={(e) => e.target.select()}
          id={`vcareConnectorData.clientSecretInput`}
          label={t('details.settings.data.clientSecret')}
          variant="outlined"
          error={!!error}
          helperText={error?.message ?? ''}
          fullWidth
          type="password"
        />
      )}
    />
  );

  if (isViewing) {
    return (
      <div className={styles.container}>
        <Typography variant="h5">{t('details.settings.connectorDataLabel')}</Typography>
        <div className={styles.fieldsWrapper}>
          <div className={styles.leftFields}>
            <Typography variant="subtitle1">{t('details.settings.data.organisationId')}:</Typography>
            <Typography variant="subtitle1">{t('details.settings.data.clientId')}:</Typography>
            <Typography variant="subtitle1">{t('details.settings.data.clientSecret')}:</Typography>
          </div>
          <div className={styles.rightFields}>
            <Typography variant="subtitle1">{connectorDetailsHook.currentSelectedConnector?.vcareConnectorData?.organisationId}</Typography>
            <Typography variant="subtitle1">{connectorDetailsHook.currentSelectedConnector?.vcareConnectorData?.clientId}</Typography>
            <Typography variant="subtitle1">{`${CLIENT_SECRET_PLACEHOLDER}${clientSecretLastFourCharacters}`}</Typography>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className={styles.container}>
      <Typography variant="h5">{t('details.settings.connectorDataLabel')}</Typography>
      <div className={styles.inputFields}>
        {organisationIdField()}
        {clientIdField()}
        {clientSecretField()}
      </div>
    </div>
  );
};

export default ConnectorSettingsData;
